import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';

const isProduction = import.meta.env.PROD === true;

const Login = () => {
  const { login, loginDemo, demoAccounts, previewMode, enterPreviewMode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const params = new URLSearchParams(location.search);
  const requestedPath = params.get('next');
  const superAdminRequired = params.get('superAdminRequired') === 'true';
  const isSuperAdminRequest = requestedPath === '/super-admin' || superAdminRequired;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');
    setResetMessage('');

    try {
      const result = await login(email.trim(), password);

      if (!result.success) {
        if (result.error === 'Invalid login credentials') {
          setError(`Feil e-post eller passord. Sjekk at brukeren finnes i Supabase. Supabase melding: ${result.error}`);
        } else if (result.error === 'Email not confirmed') {
          setError(`E-postadressen er ikke bekreftet i Supabase. Supabase melding: ${result.error}`);
        } else {
          setError(`Kunne ikke logge inn: ${result.error}`);
        }
        return;
      }

      if (isSuperAdminRequest && !result.isSuperAdmin && !previewMode) {
        setError('Denne brukeren har ikke SuperAdmin-tilgang. Rollen må settes i Supabase Auth → App Metadata.');
        return;
      }

      if (result.isSuperAdmin || (!isProduction && previewMode)) {
        navigate('/super-admin', { replace: true });
      } else {
        navigate(requestedPath || '/dashboard', { replace: true });
      }
    } catch (err) {
      setError('En uventet feil oppstod.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setError('Skriv inn e-postadressen din først.');
      return;
    }

    setResetLoading(true);
    setError('');
    setResetMessage('');

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        normalizedEmail,
        {
          redirectTo: `${window.location.origin}/#/reset-password`
        }
      );

      if (resetError) {
        setError(`Kunne ikke sende gjenopprettings-e-post: ${resetError.message}`);
        return;
      }

      setResetMessage('Vi har sendt en e-post for å sette nytt passord.');
    } catch (resetError) {
      setError(`Kunne ikke sende gjenopprettings-e-post: ${resetError.message}`);
    } finally {
      setResetLoading(false);
    }
  };

  const handleDemoLogin = async (account) => {
    if (isProduction || loading) return;

    setLoading(true);
    setError('');

    const result = await loginDemo(account.email, account.password);

    if (!result.success) {
      setError(result.error || 'Kunne ikke åpne demoen.');
      setLoading(false);
      return;
    }

    setLoading(false);
    navigate(requestedPath && requestedPath !== '/super-admin' ? requestedPath : '/dashboard', {
      replace: true
    });
  };

  const openLocalPreview = () => {
    if (isProduction) return;

    setError('');
    enterPreviewMode();
    navigate('/super-admin', { replace: true });
  };

  const planDetails = {
    start: { icon: '⚡', title: 'Start-demo', description: 'Grunnleggende CRM og pipeline' },
    pro: { icon: '🚀', title: 'Vekst-demo', description: 'Automasjon og Email Copilot' },
    enterprise: { icon: '🏢', title: 'Enterprise-demo', description: 'SSO, audit logs og dedikert support' }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-xl border border-slate-200 p-10">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-200">
            <SafeIcon icon="⚡" className="text-3xl text-white" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Velkommen</h1>
          <p className="text-slate-500 font-medium mt-2">Logg inn på Sales Work CRM</p>
        </div>

        {!isProduction && (
          <>
            <div className="mb-6">
              <p className="text-sm font-bold text-slate-800 mb-3">Prøv abonnementene</p>
              <div className="space-y-2">
                {(demoAccounts || []).map(account => {
                  const details = planDetails[account.planId];

                  return (
                    <button
                      key={account.id}
                      type="button"
                      disabled={loading}
                      onClick={() => handleDemoLogin(account)}
                      className="w-full text-left p-4 rounded-2xl border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-black text-slate-900">
                          {details.icon} {details.title}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                          {account.plan}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{details.description}</p>
                      <p className="text-[10px] text-slate-400 mt-2">
                        {account.email} · {account.password}
                      </p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400 mt-3">
                Kun utvikling/demo. Demoene bruker ikke ekte Supabase-data og lagrer ikke data permanent.
              </p>
            </div>

            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-bold text-amber-800">SuperAdmin-demo</p>
              <p className="mt-1 text-xs leading-5 text-amber-700">
                Åpne en lokal demo uten innlogging. Demoen bruker ikke ekte Supabase-data.
              </p>
              <button
                type="button"
                onClick={openLocalPreview}
                className="mt-3 w-full rounded-xl bg-amber-500 px-4 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-amber-600"
              >
                Åpne SuperAdmin uten innlogging
              </button>
            </div>
          </>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm font-bold rounded-xl flex items-start gap-3">
            <SafeIcon icon="⚠️" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {resetMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-100 text-green-700 text-sm font-bold rounded-xl">
            {resetMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">E-postadresse</label>
            <input
              required
              type="email"
              disabled={loading || resetLoading}
              className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold disabled:opacity-50"
              placeholder="navn@selskap.no"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Passord</label>
            <input
              required
              type="password"
              disabled={loading || resetLoading}
              className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold disabled:opacity-50"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={loading || resetLoading}
            className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-[0.2em] hover:bg-slate-800 transition-all shadow-xl disabled:opacity-50 active:scale-[0.98] flex justify-center items-center"
          >
            {loading ? 'Logger inn...' : 'Logg inn'}
          </button>
        </form>

        <button
          type="button"
          onClick={handlePasswordReset}
          disabled={loading || resetLoading}
          className="w-full mt-4 text-sm font-bold text-blue-600 hover:text-blue-800 disabled:opacity-50"
        >
          {resetLoading ? 'Sender e-post...' : 'Glemt passord?'}
        </button>
      </div>
    </div>
  );
};

export default Login;